import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import AuthLayout from '../../layouts/AuthLayout/AuthLayout';
import FormInput from '../../components/FormInput/FormInput';
import Button from '../../components/Button/Button';
import api from '../../services/api';
import styles from './RecoveryLogin.module.css';

function RecoveryLogin() {
  usePageTitle('Log in with recovery email');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /* ?email= param carries the ACCOUNT email when arriving from the email link */
  const accountEmailFromUrl = searchParams.get('email') || '';

  const [step, setStep]             = useState(accountEmailFromUrl ? 2 : 1);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [accountEmail, setAccountEmail]   = useState(accountEmailFromUrl);
  const [maskedEmail, setMaskedEmail]     = useState('');
  const [otp, setOtp]                     = useState('');
  const [error, setError]                 = useState('');
  const [loading, setLoading]             = useState(false);

  /* Arriving via email link — PIN was already sent; just show step 2 */

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!recoveryEmail.trim()) { setError('Your email is required.'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/recovery-request', { recoveryEmail });
      setMaskedEmail(data.maskedEmail);
      setAccountEmail(data.accountEmail);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!otp.trim()) { setError('Please enter the PIN.'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/recovery-login', { accountEmail, otp });
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('isRecoverySession', 'true');
      localStorage.setItem('user', JSON.stringify({
        userId:               data.userId,
        firstName:            data.firstName,
        lastName:             data.lastName,
        email:                data.email,
        isAdmin:              data.isAdmin,
        userType:             data.userType,
        companyId:            data.companyId,
        accessLevel:          data.accessLevel,
        contractSigned:       data.contractSigned,
        recoveryContactEmail: data.recoveryContactEmail || '',
      }));
      navigate(data.isAdmin ? '/dashboard/overview' : '/dashboard/assets');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Link to="/login" className={styles.backLink}>
        <ArrowLeft size={16} />
        Log in with recovery email
      </Link>

      {step === 1 ? (
        <>
          <p className={styles.description}>
            Enter your email address. A one-time PIN will be sent to you if you're registered as a recovery contact.
          </p>
          <form onSubmit={handleRequest}>
            <FormInput
              id="recoveryEmail"
              label="Your email"
              type="email"
              placeholder="name@email.com"
              value={recoveryEmail}
              onChange={e => { setRecoveryEmail(e.target.value); setError(''); }}
              error={error}
            />
            <Button type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send PIN'}
            </Button>
          </form>
        </>
      ) : (
        <>
          <p className={styles.description}>
            {maskedEmail
              ? <>A one-time PIN was sent to <strong>{maskedEmail}</strong> to grant you access in the admin's absence.</>
              : 'Enter the one-time PIN from the email you received.'}
          </p>
          <form onSubmit={handleLogin}>
            <FormInput
              id="otp"
              label="One-time PIN"
              type="text"
              placeholder=""
              value={otp}
              onChange={e => { setOtp(e.target.value); setError(''); }}
              error={error}
              inputMode="numeric"
              maxLength={6}
            />
            <Button type="submit" disabled={loading}>
              {loading ? 'Logging in…' : 'Log in'}
            </Button>
            <button
              type="button"
              className={styles.resendBtn}
              onClick={() => { setStep(1); setOtp(''); setError(''); }}
            >
              Resend PIN
            </button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}

export default RecoveryLogin;
